import { useEffect, useState } from 'react';
import {
  fetchMission,
  patchMission,
  type MissionView,
  type Objective,
  type PatchOp,
} from '../api/missions';
import { errorText } from './useSessionLive';

/** What a session screen may change about an objective without leaving the page. */
export type ObjectivePatch = Pick<Objective, 'title' | 'type' | 'priority' | 'radius'>;

/**
 * Inline editing of an objective from a session screen. A session serves the
 * mission's current definition, so the change is a patch on the mission, sent
 * against the revision this hook holds; the permission is the mission's own
 * (`canEdit`, decided by the platform), not the session's. An objective the
 * direction added at runtime is not in the mission, so it is not editable here.
 */
export function useObjectiveEditor(
  missionCode: string,
  enabled: boolean,
  onError: (text: string | null) => void,
  onSaved: () => void,
) {
  const [mission, setMission] = useState<MissionView | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    // No access to the mission is not an error on a session page: it only means no editing.
    fetchMission(missionCode)
      .then((m) => alive && setMission(m))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [missionCode, enabled]);

  const canEdit = (id: string) =>
    !!mission?.canEdit && mission.definition.objectives.some((o) => o.id === id);

  const save = (id: string, patch: ObjectivePatch) => {
    if (!mission) return Promise.resolve();
    const index = mission.definition.objectives.findIndex((o) => o.id === id);
    if (index < 0) return Promise.resolve();
    const ops: PatchOp[] = Object.entries(patch).map(([field, value]) => ({
      path: `/objectives/${index}/${field}`,
      value,
    }));
    return patchMission(mission.code, mission.revision, ops)
      .then((m) => {
        setMission(m);
        onError(null);
        onSaved();
      })
      .catch((e) => {
        onError(errorText(e));
        // Whatever went wrong, the revision we hold is the first suspect.
        return fetchMission(missionCode)
          .then(setMission)
          .catch(() => undefined);
      });
  };

  return { canEdit, save };
}
